import { useCallback, useEffect, useRef, useState } from "react";
import { CommunityDiscussComments } from "../../pages/community/models/CommunityDiscussComments";
import { CommunityMediaModel } from "../../pages/community/models/CommunityMediaModel";
import { CommunityAdminBanType } from "../../pages/communityAdmin/enums/CommunityAdminBanType";
import { CommunityAdminReviewModel } from "../../pages/communityAdmin/models/CommunityAdminReviewModel";
import { addComments, adminDiscussionDetails, banForum, banUser, deleteComments, deleteResources, getComments, getResources, toggleReact } from "../../services/communityService";
import { AdminDiscussDetailModel } from "./models/AdminDiscussDetailModel";
import { ApiArrayResponseModel } from "./models/ApiResponseModel";
import { CommentModel } from "./models/CommentModel";
import { ResourcesModel } from "./models/ResourcesModel";

export const useCommunityReview = (id?: number | string) => {
    const isMountedRef = useRef(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | boolean | null>(null);
    const [details, setDetails] = useState<CommunityAdminReviewModel | null>(null);
    

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        }
    }, []);

    const load = useCallback(async () => {
        if(!id)
            return;
        try {
            setLoading(true);
            // setDetails(null);
            setError(null);

            const [details, comments, resources] = await Promise.all([
                adminDiscussionDetails(id) as Promise<AdminDiscussDetailModel | null>,
                getComments(id) as Promise<CommentModel[]>,
                getResources(id) as Promise<ApiArrayResponseModel<ResourcesModel>>
            ]);
            if(!isMountedRef.current)
                return;
            if(!details) {
                setError(true);
                return;
            }
            const _parsed: CommunityAdminReviewModel = {
                id: details.id,
                type: details.postType === 'circle' ? 'Mastermind Circle': 'Open Forum',
                author: {
                    avatar: details.authorAvatar || (details as any).hostDetail?.avatar,
                    username: details.fullName || (details as any).hostDetail?.fullname || (details as any).hostDetail?.fullName || (details as any).authorName || (details as any).username || '',
                    isMine: details.host ?? false
                },
                circles: {
                    quantity: details.invitesDetail.length,
                    previewAvatars: details.invitesDetail.map((i) => i.avatar)
                },
                tags: details.tags,
                comments: comments.map(c => ({ 
                        id: c.id,
                        authorId: c.authorId,
                        author: c.authorName,
                        avatar: c.authorAvatar,
                        content: c.content,
                        createdAt: c.createdAt,
                        isMine: c.isHost,
                        isLiked: c.isLiked,
                        reactions: c.likesCount,
                        files: c.resources as any[],
                        reply: !!c.parentData ? {
                            id: c.parentData.id,
                            content: c.parentData.content,
                            author: c.parentData.fullName,
                            avatar: c.parentData.authorAvatar,
                            isMine: c.parentData.isMine
                        }: undefined
                    })) as CommunityDiscussComments[],
                mediafiles: resources.results.map(r => ({ 
                    id: r.id, 
                    filename: r.title, 
                    bytes: r.size, 
                    author: r.authorName, 
                    createdAt: r.createdAt, 
                    type: r.resourceType,
                    url: r.file 
                })) as CommunityMediaModel[],
                experiences: [],
                content: details.description,
                topic: details.title,
                facilitator: details.facilitatorDetail ? {
                    avatar: details.facilitatorDetail.avatar,
                    username: details.facilitatorDetail.fullName,
                    isTeam: true
                }: undefined
            };
            setDetails(_parsed);
        }
        catch (error) {
            if(!isMountedRef.current)
                return;
            setError(error as Error ?? true);
        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const addComment = useCallback(async (content: string, reply?: number, filesCb?: (commentId: number) => Promise<void>, postCb?: () => void) => {
        if(!id)
            return;
        try {
            setLoading(true);
            const _comment = await addComments(id, content, reply ?? null);
            if(filesCb && !!_comment.id)
                await filesCb(_comment.id);
            if(!!postCb) {
                postCb();
            }
            await load();
        }
        catch {

        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, []);

    const deleteComment = useCallback(async (commentId: number) => {
        try {
            setLoading(true);
            await deleteComments(commentId);
            await load();
        }
        finally{
                if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, []);

    const react = useCallback(async (comment_id: number | string) => {
        if(!id)
            return;
        try {
            setLoading(true);
            await toggleReact(id, comment_id);
            await load();
        }
        catch {

        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false);
        }
    }, []);

    const toBanUser = useCallback(async (id: number, banType: CommunityAdminBanType, duration: number, reason: string) => {
        try {
            setLoading(true);
            await banUser(id, banType, duration, reason );
        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false); 
        }
    }, []);

    const toBanForum = useCallback(async (id: number, banType: CommunityAdminBanType, duration: number, reason: string) => {
        try {
            setLoading(true);
            await banForum(id, banType, duration, reason);
        }
        finally {
            if(!isMountedRef.current)
                return;
            setLoading(false); 
        }
    }, []);

    const deleteResource = useCallback(async (id: number) => {
        try {
            setLoading(true);
            await deleteResources(id);
            await load();
        }
        catch {}
        finally {
            setLoading(false);
        }
    }, [])

    return { details, loading, error, reload: load, addComment, deleteComment, react, toBanUser, toBanForum, deleteResource }
}